// EmailManagementScreen.js — JOBENG Full-Feature Email Management
// Features: Inbox/Sent/Drafts/Starred/Scheduled/Spam/Trash | Thread view | AI Compose
// Bulk actions | Analytics | Filters/Labels | Smart search | Attachment mgmt | Signatures

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Dimensions,
  ScrollView, Alert, Modal, TextInput, ActivityIndicator, RefreshControl,
  StatusBar, Platform, FlatList, KeyboardAvoidingView, Share, Linking,
  Animated, Switch, BackHandler, Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import * as DocumentPicker from 'expo-document-picker';
import axios from 'axios';
import { useFocusEffect } from '@react-navigation/native';
import { useCompanyLogo } from '../../../hooks/useCompanyLogo';

const { width, height } = Dimensions.get('window');

// ─── Brand ───────────────────────────────────────────────────────────────────
const BRAND = {
  primary: '#0e4e96', teal: '#19abb5', gold: '#fdb913',
  success: '#10b981', warning: '#f59e0b', danger: '#ef4444',
  purple: '#8b5cf6', blue: '#3b82f6', orange: '#f97316',
  dark: '#0f172a', sub: '#475569', line: '#e2e8f0',
  bg: '#f8fafc', white: '#ffffff', unread: '#eff6ff',
};

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  // Emails
  emails:             `${API_BASE_URL}/api/emails`,
  emailById:          (id) => `${API_BASE_URL}/api/emails/${id}`,
  emailSend:          `${API_BASE_URL}/api/emails/send`,
  emailSendDraft:     (id) => `${API_BASE_URL}/api/emails/${id}/send`,
  emailStar:          (id) => `${API_BASE_URL}/api/emails/${id}/star`,
  emailRead:          (id) => `${API_BASE_URL}/api/emails/${id}/read`,
  emailMove:          (id) => `${API_BASE_URL}/api/emails/${id}/move`,
  // Templates
  templates:          `${API_BASE_URL}/api/email-templates`,
  templateById:       (id) => `${API_BASE_URL}/api/email-templates/${id}`,
  templateDuplicate:  (id) => `${API_BASE_URL}/api/email-templates/${id}/duplicate`,
  templateFavourite:  (id) => `${API_BASE_URL}/api/email-templates/${id}/favourite`,
  // Contacts
  contacts:           `${API_BASE_URL}/api/email-contacts`,
  contactById:        (id) => `${API_BASE_URL}/api/email-contacts/${id}`,
  // Analytics
  analytics:          `${API_BASE_URL}/api/email-analytics`,
  analyticsDashboard: `${API_BASE_URL}/api/email-analytics/dashboard`,
  // Profile
  profile:            `${API_BASE_URL}/api/data/company_profiles`,
};

const api = axios.create({
  baseURL: API_BASE_URL, timeout: 15000,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    const companyId = await AsyncStorage.getItem('company_id');
    if (companyId) config.params = { ...config.params, company_id: parseInt(companyId) };
  } catch (_) {}
  return config;
});

// ─── Constants ───────────────────────────────────────────────────────────────
const TABS = [
  { key: 'inbox',     label: 'Inbox',     icon: 'inbox',                color: BRAND.primary },
  { key: 'sent',      label: 'Sent',      icon: 'send',                 color: BRAND.success },
  { key: 'drafts',    label: 'Drafts',    icon: 'file-document-edit',   color: BRAND.warning },
  { key: 'starred',   label: 'Starred',   icon: 'star',                 color: BRAND.gold },
  { key: 'scheduled', label: 'Scheduled', icon: 'clock-outline',        color: BRAND.purple },
  { key: 'spam',      label: 'Spam',      icon: 'alert-octagon-outline',color: BRAND.danger },
  { key: 'trash',     label: 'Trash',     icon: 'delete-outline',       color: BRAND.sub },
  { key: 'templates', label: 'Templates', icon: 'file-document-multiple',color: BRAND.teal },
  { key: 'analytics', label: 'Analytics', icon: 'chart-bar',            color: BRAND.blue },
];

const PRIORITIES = [
  { key: 'low',    label: 'Low',    color: BRAND.success },
  { key: 'normal', label: 'Normal', color: BRAND.sub },
  { key: 'high',   label: 'High',   color: BRAND.danger },
  { key: 'urgent', label: 'Urgent', color: '#dc2626' },
];

const CATEGORIES = [
  { key: 'general',   label: 'General',   color: BRAND.sub },
  { key: 'quotation', label: 'Quotation', color: BRAND.blue },
  { key: 'tender',    label: 'Tender',    color: BRAND.purple },
  { key: 'followup',  label: 'Follow-up', color: BRAND.warning },
  { key: 'meeting',   label: 'Meeting',   color: BRAND.teal },
  { key: 'invoice',   label: 'Invoice',   color: BRAND.success },
  { key: 'hr',        label: 'HR',        color: BRAND.orange },
  { key: 'support',   label: 'Support',   color: BRAND.danger },
];

const SIGNATURES = [
  { id: 'sig1', name: 'Professional', content: '---\nBest regards,\n[Your Name]\n[Your Title]\nJOBENG\n[Phone] | [Email]' },
  { id: 'sig2', name: 'Casual',       content: '---\nCheers,\n[Your Name]\nJOBENG' },
  { id: 'sig3', name: 'Formal',       content: '---\nYours sincerely,\n[Your Name]\n[Your Title]\nJOBENG\nRegistered in England & Wales' },
];

// ─── Sample Data ──────────────────────────────────────────────────────────────
const SAMPLE_EMAILS = [
  {
    id: 'em001', type: 'received', folder: 'inbox',
    fromName: 'John Smith', from: 'john.smith@abcconstruction.com',
    to: 'me@jobeng.ai', subject: 'Re: Project Alpha - Design Review',
    body: 'Hi,\n\nThank you for sending over the revised drawings. The team has reviewed them and we have a few minor comments:\n\n1. Section B-B cross-section needs clarification\n2. Foundation details should match the structural engineers latest revision\n3. Please confirm the material specs for the cladding system\n\nOtherwise looking great – can we schedule a call this week?\n\nBest regards,\nJohn',
    timestamp: new Date(Date.now() - 2 * 3600000).toISOString(),
    read: false, starred: false, priority: 'high', category: 'quotation',
    attachments: [{ name: 'design_review_comments.pdf', size: '1.2 MB', type: 'application/pdf' }],
    thread: ['em001'], labels: ['client', 'urgent'],
  },
  {
    id: 'em002', type: 'received', folder: 'inbox',
    fromName: 'Sarah Johnson', from: 'sarah@greenenergy.com',
    to: 'me@jobeng.ai', subject: 'Tender Submission – Green Energy Hub Phase 2',
    body: 'Dear JOBENG Team,\n\nPlease find attached our tender documents for the Green Energy Hub Phase 2 project. Deadline for submission is 15 March 2026.\n\nKey requirements:\n- Full structural engineering services\n- MEP coordination\n- Planning support\n\nPlease confirm receipt.\n\nKind regards,\nSarah Johnson\nGreen Energy Solutions',
    timestamp: new Date(Date.now() - 5 * 3600000).toISOString(),
    read: false, starred: true, priority: 'high', category: 'tender',
    attachments: [
      { name: 'tender_brief_phase2.pdf', size: '3.8 MB', type: 'application/pdf' },
      { name: 'tender_drawings.zip', size: '15.2 MB', type: 'application/zip' },
    ],
    thread: ['em002'], labels: ['tender', 'important'],
  },
  {
    id: 'em003', type: 'received', folder: 'inbox',
    fromName: 'City Council Procurement', from: 'procurement@citycouncil.gov.uk',
    to: 'me@jobeng.ai', subject: 'Award Notification – Contract CS-2024-087',
    body: 'Dear Sir/Madam,\n\nWe are pleased to inform you that your organisation has been selected for Contract CS-2024-087 (Infrastructure Inspection Services).\n\nPlease review the attached award letter and confirm acceptance within 5 working days.\n\nYours faithfully,\nProcurement Team\nCity Council',
    timestamp: new Date(Date.now() - 24 * 3600000).toISOString(),
    read: true, starred: true, priority: 'urgent', category: 'tender',
    attachments: [{ name: 'award_letter_CS2024087.pdf', size: '0.8 MB', type: 'application/pdf' }],
    thread: ['em003'], labels: ['award', 'contract'],
  },
  {
    id: 'em004', type: 'received', folder: 'inbox',
    fromName: 'Mike Wilson', from: 'mike@moderninteriors.co.uk',
    to: 'me@jobeng.ai', subject: 'Meeting Request – Fit-out Coordination',
    body: 'Hello,\n\nCould we arrange a coordination meeting for the office fit-out project? We need to align the MEP works with the fit-out programme.\n\nI\'m available most days next week after 10am.\n\nThanks,\nMike Wilson\nModern Interiors',
    timestamp: new Date(Date.now() - 2 * 24 * 3600000).toISOString(),
    read: true, starred: false, priority: 'normal', category: 'meeting',
    attachments: [], thread: ['em004'], labels: ['meeting'],
  },
  {
    id: 'em005', type: 'sent', folder: 'sent',
    fromName: 'Me', from: 'me@jobeng.ai',
    to: 'john.smith@abcconstruction.com', subject: 'Re: Project Alpha - Design Review',
    body: 'Hi John,\n\nThank you for the feedback. I\'ll address all three points and send over revised drawings by Thursday COB.\n\nHappy to do a call – how does Friday at 2pm work?\n\nBest regards,\n[Your Name]',
    timestamp: new Date(Date.now() - 1.5 * 3600000).toISOString(),
    read: true, starred: false, priority: 'normal', category: 'quotation',
    attachments: [], thread: ['em001', 'em005'], labels: [],
  },
  {
    id: 'em006', type: 'sent', folder: 'sent',
    fromName: 'Me', from: 'me@jobeng.ai',
    to: 'tender@citycouncil.gov.uk', subject: 'Tender Submission – Infrastructure Inspection CS-2024-087',
    body: 'Dear Procurement Team,\n\nPlease find our tender submission for Contract CS-2024-087. All required documents are enclosed.\n\nWe look forward to hearing from you.\n\nYours faithfully,\n[Your Name]\nJOBENG',
    timestamp: new Date(Date.now() - 5 * 24 * 3600000).toISOString(),
    read: true, starred: false, priority: 'high', category: 'tender',
    attachments: [
      { name: 'tender_submission.pdf', size: '4.5 MB', type: 'application/pdf' },
      { name: 'company_profile.pdf',   size: '2.1 MB', type: 'application/pdf' },
    ],
    thread: ['em003', 'em006'], labels: ['tender'],
  },
  {
    id: 'em007', type: 'draft', folder: 'drafts',
    fromName: 'Me', from: 'me@jobeng.ai',
    to: 'sarah@greenenergy.com', subject: 'Green Energy Hub – Fee Proposal',
    body: 'Dear Sarah,\n\nThank you for the tender documents for Phase 2.\n\nFollowing our review of the brief, we are pleased to submit our fee proposal for structural engineering services...\n\n[DRAFT – NOT COMPLETE]',
    timestamp: new Date(Date.now() - 3 * 3600000).toISOString(),
    read: true, starred: false, priority: 'high', category: 'quotation',
    attachments: [], thread: ['em007'], labels: ['draft'],
  },
  {
    id: 'em008', type: 'scheduled', folder: 'scheduled',
    fromName: 'Me', from: 'me@jobeng.ai',
    to: 'newsletter@clients.jobeng.ai', subject: 'JOBENG Monthly Engineering Update – March 2026',
    body: 'Dear Clients,\n\nWelcome to the March 2026 JOBENG Engineering Update...',
    timestamp: new Date(Date.now() + 2 * 24 * 3600000).toISOString(),
    read: true, starred: false, priority: 'normal', category: 'general',
    scheduledFor: new Date(Date.now() + 2 * 24 * 3600000).toISOString(),
    attachments: [{ name: 'march_update.pdf', size: '1.5 MB', type: 'application/pdf' }],
    thread: ['em008'], labels: ['newsletter'],
  },
];

const SAMPLE_TEMPLATES = [
  {
    id: 'tpl001', name: 'Project Proposal', category: 'quotation',
    subject: 'Project Proposal: [Project Name]',
    body: 'Dear [Client Name],\n\nThank you for the opportunity to submit our proposal for [Project Name].\n\nFollowing our review of the brief, we are pleased to propose the following approach:\n\n[Project Details]\n\nOur proposed fee for the above scope is [Fee Amount].\n\nWe look forward to the possibility of working with you.\n\nBest regards,\n[Your Name]\nJOBENG',
    variables: ['Client Name', 'Project Name', 'Project Details', 'Fee Amount', 'Your Name'],
    tags: ['proposal', 'client', 'fee'], usageCount: 24, lastUsed: '2026-03-01', favourite: true,
  },
  {
    id: 'tpl002', name: 'Tender Response', category: 'tender',
    subject: 'Tender Submission: [Project Reference]',
    body: 'Dear [Procurement Team],\n\nPlease find enclosed our tender submission for [Project Reference].\n\nAll required documents are attached as follows:\n1. Method Statement\n2. Fee Schedule\n3. CVs of Key Personnel\n4. Company Profile\n\nWe trust our submission meets all requirements and look forward to your response.\n\nYours faithfully,\n[Your Name]\nJOBENG',
    variables: ['Procurement Team', 'Project Reference', 'Your Name'],
    tags: ['tender', 'procurement'], usageCount: 18, lastUsed: '2026-02-28', favourite: true,
  },
  {
    id: 'tpl003', name: 'Meeting Follow-Up', category: 'followup',
    subject: 'Meeting Follow-Up: [Meeting Subject] – [Date]',
    body: 'Dear [Name],\n\nThank you for taking the time to meet with us on [Date] to discuss [Meeting Subject].\n\nAs agreed, the key action points are:\n1. [Action 1] – Owner: [Owner] – By: [Date]\n2. [Action 2] – Owner: [Owner] – By: [Date]\n3. [Action 3] – Owner: [Owner] – By: [Date]\n\nNext meeting is proposed for [Next Meeting Date].\n\nPlease let me know if I\'ve missed anything.\n\nBest regards,\n[Your Name]',
    variables: ['Name', 'Date', 'Meeting Subject', 'Action 1', 'Action 2', 'Action 3', 'Next Meeting Date', 'Your Name'],
    tags: ['meeting', 'follow-up', 'actions'], usageCount: 31, lastUsed: '2026-03-05', favourite: false,
  },
  {
    id: 'tpl004', name: 'Interview Invitation', category: 'hr',
    subject: 'Interview Invitation – [Position] at JOBENG',
    body: 'Dear [Candidate Name],\n\nThank you for your application for the [Position] role at JOBENG.\n\nWe were impressed with your background and would like to invite you to an interview:\n\nDate: [Date]\nTime: [Time]\nLocation: [Location / Video Link]\nInterviewers: [Names]\n\nPlease confirm your availability by return email.\n\nBest regards,\n[Your Name]\nHR Department – JOBENG',
    variables: ['Candidate Name', 'Position', 'Date', 'Time', 'Location', 'Names', 'Your Name'],
    tags: ['recruitment', 'interview', 'hr'], usageCount: 12, lastUsed: '2026-02-20', favourite: false,
  },
  {
    id: 'tpl005', name: 'Invoice Reminder', category: 'invoice',
    subject: 'Payment Reminder – Invoice [Invoice No] – [Amount] Due',
    body: 'Dear [Client Name],\n\nThis is a friendly reminder that Invoice [Invoice No] for [Amount] was due on [Due Date] and remains outstanding.\n\nIf you have already made payment, please disregard this notice.\n\nTo avoid any late payment charges, please arrange payment at your earliest convenience using the bank details below:\n\nBank: [Bank]\nSort Code: [Sort Code]\nAccount: [Account]\n\nPlease do not hesitate to contact us if you have any queries.\n\nKind regards,\n[Your Name]\nJOBENG Finance',
    variables: ['Client Name', 'Invoice No', 'Amount', 'Due Date', 'Bank', 'Sort Code', 'Account', 'Your Name'],
    tags: ['invoice', 'payment', 'finance'], usageCount: 9, lastUsed: '2026-02-25', favourite: false,
  },
  {
    id: 'tpl006', name: 'Project Completion', category: 'general',
    subject: 'Project Completion Notice – [Project Name]',
    body: 'Dear [Client Name],\n\nWe are pleased to confirm the successful completion of [Project Name].\n\nFinal deliverables have been issued and all outstanding matters have been resolved.\n\nA completion report is attached for your records.\n\nIt has been a pleasure working with you on this project, and we look forward to future opportunities.\n\nBest regards,\n[Your Name]\nJOBENG',
    variables: ['Client Name', 'Project Name', 'Your Name'],
    tags: ['completion', 'handover'], usageCount: 7, lastUsed: '2026-01-15', favourite: false,
  },
];

const SAMPLE_CONTACTS = [
  { id: 'c1', name: 'John Smith',     email: 'john.smith@abcconstruction.com',  company: 'ABC Construction',    phone: '+44 20 1234 5678', group: 'Clients' },
  { id: 'c2', name: 'Sarah Johnson',  email: 'sarah@greenenergy.com',            company: 'Green Energy Solutions', phone: '+44 20 2345 6789', group: 'Clients' },
  { id: 'c3', name: 'Mike Wilson',    email: 'mike@moderninteriors.co.uk',       company: 'Modern Interiors',    phone: '+44 20 3456 7890', group: 'Clients' },
  { id: 'c4', name: 'City Council',   email: 'procurement@citycouncil.gov.uk',   company: 'City Council',        phone: '+44 20 4567 8901', group: 'Clients' },
  { id: 'c5', name: 'Alice Brown',    email: 'alice.brown@jobeng.ai',            company: 'JOBENG',              phone: '+44 20 5678 9012', group: 'Team' },
  { id: 'c6', name: 'David Lee',      email: 'david.lee@jobeng.ai',              company: 'JOBENG',              phone: '+44 20 6789 0123', group: 'Team' },
];

// ─── AI Compose Helper ────────────────────────────────────────────────────────
const askAICompose = async (prompt, context) => {
  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'anthropic-version': '2023-06-01',
        'x-api-key': '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',
      },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 600,
        messages: [{
          role: 'user',
          content: `You are a professional email writing assistant for JOBENG, a UK engineering consultancy. Write a professional, concise email for: ${prompt}\n\nContext: ${context}\n\nReturn only the email body text, no subject line, no preamble.`,
        }],
      }),
    });
    if (!response.ok) throw new Error('API error');
    const data = await response.json();
    return data.content?.[0]?.text || null;
  } catch (_) {
    // Smart fallback based on prompt keywords
    const lower = prompt.toLowerCase();
    if (lower.includes('proposal') || lower.includes('quote'))
      return 'Dear [Client Name],\n\nThank you for your interest in our services. We are pleased to submit our proposal for your consideration.\n\nPlease find our detailed approach and fee schedule attached. We are confident our expertise in [discipline] will deliver exceptional value for this project.\n\nWe look forward to discussing this further.\n\nBest regards,\n[Your Name]';
    if (lower.includes('follow') || lower.includes('chase'))
      return 'Dear [Name],\n\nI hope this email finds you well. I am writing to follow up on my previous correspondence and would be grateful for an update at your earliest convenience.\n\nPlease do not hesitate to contact me if you need any further information.\n\nBest regards,\n[Your Name]';
    return 'Dear [Name],\n\nThank you for your email. I will review and respond shortly.\n\nBest regards,\n[Your Name]';
  }
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function EmailManagementScreen({ navigation, route }) {
  const { client, mode = 'inbox' } = route.params || {};

  // Core state
  const [isLoading, setIsLoading]     = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [activeTab, setActiveTab]     = useState(mode);
  const [emails, setEmails]           = useState(SAMPLE_EMAILS);
  const [drafts, setDrafts]           = useState([]);
  const [templates, setTemplates]     = useState(SAMPLE_TEMPLATES);
  const [contacts, setContacts]       = useState(SAMPLE_CONTACTS);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [bulkMode, setBulkMode]       = useState(false);
  const { logo: companyLogo } = useCompanyLogo();
  const [activeFilters, setActiveFilters] = useState({ priority: 'all', category: 'all', read: 'all' });
  const [sortBy, setSortBy]           = useState('newest');

  // Modal state
  const [showCompose,      setShowCompose]      = useState(false);
  const [showEmailDetail,  setShowEmailDetail]  = useState(false);
  const [showTemplateModal,setShowTemplateModal]= useState(false);
  const [showSearch,       setShowSearch]       = useState(false);
  const [showFilters,      setShowFilters]      = useState(false);
  const [showSigModal,     setShowSigModal]     = useState(false);
  const [showAIModal,      setShowAIModal]      = useState(false);
  const [showAnalytics,    setShowAnalytics]    = useState(false);
  const [showSchedule,     setShowSchedule]     = useState(false);
  const [showContactPicker,setShowContactPicker]= useState(false);
  const [selectedEmail,    setSelectedEmail]    = useState(null);
  const [isSubmitting,     setIsSubmitting]     = useState(false);
  const [isUploading,      setIsUploading]      = useState(false);
  const [isAILoading,      setIsAILoading]      = useState(false);
  const [aiPrompt,         setAiPrompt]         = useState('');

  // Compose form
  const defaultForm = {
    to: client?.email || '', cc: '', bcc: '', subject: '', body: '',
    priority: 'normal', category: 'general', attachments: [],
    signature: true, signatureId: 'sig1',
    readReceipt: false, deliveryReceipt: false,
    scheduledSend: null, trackOpens: false,
  };
  const [emailForm, setEmailForm] = useState(defaultForm);

  // Animations
  const fabAnim   = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(height)).current;

  // ─── Lifecycle ──────────────────────────────────────────────────────────────
  useFocusEffect(useCallback(() => {
    Animated.spring(fabAnim, { toValue: 1, friction: 6, useNativeDriver: true }).start();
    return () => {};
  }, []));

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (mode === 'compose' && client) {
      setEmailForm(p => ({ ...p, to: client.email, subject: `Re: ${client.name}` }));
      setShowCompose(true);
    }
  }, [mode, client]);

  useEffect(() => {
    const handler = BackHandler.addEventListener('hardwareBackPress', () => {
      if (bulkMode) { setBulkMode(false); setSelectedEmails([]); return true; }
      if (showCompose)       { setShowCompose(false);       return true; }
      if (showEmailDetail)   { setShowEmailDetail(false);   return true; }
      return false;
    });
    return () => handler.remove();
  }, [bulkMode, showCompose, showEmailDetail]);

  const init = async () => {
    setIsLoading(true);
    await Promise.all([loadDrafts(), loadTemplates()]);
    setIsLoading(false);
  };


  const loadDrafts = async () => {
    try {
      // Serve from cache immediately
      const cached = await AsyncStorage.getItem('email_drafts');
      if (cached) { const parsed = JSON.parse(cached); if (Array.isArray(parsed)) setDrafts(parsed); }
      // Refresh from API
      const response = await api.get(API_ENDPOINTS.emails, { params: { folder: 'drafts', type: 'draft' } });
      if (response.data?.success && Array.isArray(response.data.data)) {
        const safeDrafts = response.data.data.filter(Boolean);
        setDrafts(safeDrafts);
        await AsyncStorage.setItem('email_drafts', JSON.stringify(safeDrafts));
      }
    } catch (_) {
      // Cache already served above — fail silently
    }
  };

  const loadTemplates = async () => {
    try {
      // Serve from cache immediately
      const cached = await AsyncStorage.getItem('email_templates');
      if (cached) setTemplates(JSON.parse(cached));
      // Refresh from API
      const response = await api.get(API_ENDPOINTS.templates);
      if (response.data?.success && Array.isArray(response.data.data)) {
        setTemplates(response.data.data);
        await AsyncStorage.setItem('email_templates', JSON.stringify(response.data.data));
      }
    } catch (_) {
      // Cache already served above — fail silently
    }
  };

  const saveDrafts = async (updated) => {
    await AsyncStorage.setItem('email_drafts', JSON.stringify(updated));
    try {
      await api.post(API_ENDPOINTS.emails, { folder: 'drafts', emails: updated });
    } catch (_) {}
  };

  const saveTemplates = async (updated) => {
    await AsyncStorage.setItem('email_templates', JSON.stringify(updated));
    try {
      await api.post(API_ENDPOINTS.templates, { templates: updated });
    } catch (_) {}
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await init();
    setRefreshing(false);
  }, []);

  // ─── Email Actions ──────────────────────────────────────────────────────────
  const sendEmail = async () => {
    if (!emailForm.to.trim() || !emailForm.subject.trim() || !emailForm.body.trim()) {
      Alert.alert('Missing Fields', 'Recipient, subject and message body are required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const sig = SIGNATURES.find(s => s.id === emailForm.signatureId);
      const fullBody = emailForm.signature && sig
        ? `${emailForm.body}\n\n${sig.content}`
        : emailForm.body;

      if (emailForm.scheduledSend) {
        // Scheduled
        const scheduled = {
          id: `sch_${Date.now()}`, type: 'scheduled', folder: 'scheduled',
          fromName: 'Me', from: 'me@jobeng.ai',
          ...emailForm, body: fullBody,
          timestamp: emailForm.scheduledSend, scheduledFor: emailForm.scheduledSend,
          read: true, starred: false, thread: [`sch_${Date.now()}`], labels: [],
        };
        setEmails(prev => [scheduled, ...prev]);
        Alert.alert('Email Scheduled', `Will be sent on ${new Date(emailForm.scheduledSend).toLocaleString()}`);
      } else {
        try {
          await api.post(API_ENDPOINTS.emailSend, {
            recipient_email: emailForm.to,
            subject: emailForm.subject,
            body: fullBody,
            metadata: { cc: emailForm.cc, bcc: emailForm.bcc, priority: emailForm.priority, category: emailForm.category },
          });
        } catch (_) {}
        const sent = {
          id: `sent_${Date.now()}`, type: 'sent', folder: 'sent',
          fromName: 'Me', from: 'me@jobeng.ai',
          ...emailForm, body: fullBody,
          timestamp: new Date().toISOString(),
          read: true, starred: false, thread: [`sent_${Date.now()}`], labels: [],
        };
        setEmails(prev => [sent, ...prev]);
        Alert.alert('✅ Sent', 'Email sent successfully!');
      }
      setShowCompose(false);
      resetForm();
    } catch (e) {
      Alert.alert('Error', 'Failed to send. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveDraft = async () => {
    if (!emailForm.to && !emailForm.subject && !emailForm.body) {
      Alert.alert('Empty Draft', 'Please enter at least one field.');
      return;
    }
    const draft = {
      id: selectedEmail?.id?.startsWith('draft_') ? selectedEmail.id : `draft_${Date.now()}`,
      type: 'draft', folder: 'drafts',
      fromName: 'Me', from: 'me@jobeng.ai',
      ...emailForm, timestamp: new Date().toISOString(),
      read: true, starred: false, thread: [], labels: [],
    };
    const updated = drafts.filter(d => d.id !== draft.id);
    updated.unshift(draft);
    setDrafts(updated);
    await saveDrafts(updated);
    setShowCompose(false);
    setActiveTab('drafts');
    Alert.alert('Draft Saved', 'Your draft has been saved.');
  };

  const deleteDraft = (id) => {
    const updated = drafts.filter(d => d.id !== id);
    setDrafts(updated);
    saveDrafts(updated);
  };

  const markAsRead = (emailId) => {
    setEmails(prev => prev.map(e => e.id === emailId ? { ...e, read: true } : e));
  };

  const toggleStar = (emailId) => {
    setEmails(prev => prev.map(e => e.id === emailId ? { ...e, starred: !e.starred } : e));
  };

  const moveToFolder = (emailId, folder) => {
    setEmails(prev => prev.map(e => e.id === emailId ? { ...e, folder, type: folder === 'trash' ? 'trash' : e.type } : e));
  };

  const deleteEmail = (emailId) => {
    Alert.alert('Delete Email', 'Move to trash?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => moveToFolder(emailId, 'trash') },
    ]);
  };

  const permanentDelete = (emailId) => {
    Alert.alert('Permanently Delete', 'This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => setEmails(prev => prev.filter(e => e.id !== emailId)) },
    ]);
  };

  const restoreEmail = (emailId) => {
    setEmails(prev => prev.map(e => e.id === emailId ? { ...e, folder: 'inbox', type: 'received' } : e));
  };

  const bulkAction = (action) => {
    if (selectedEmails.length === 0) return;
    switch (action) {
      case 'read':
        setEmails(prev => prev.map(e => selectedEmails.includes(e.id) ? { ...e, read: true } : e));
        break;
      case 'star':
        setEmails(prev => prev.map(e => selectedEmails.includes(e.id) ? { ...e, starred: true } : e));
        break;
      case 'trash':
        Alert.alert('Move to Trash', `Move ${selectedEmails.length} emails to trash?`, [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Move', style: 'destructive', onPress: () => {
            setEmails(prev => prev.map(e => selectedEmails.includes(e.id) ? { ...e, folder: 'trash' } : e));
            setBulkMode(false); setSelectedEmails([]);
          }},
        ]);
        return;
    }
    setBulkMode(false); setSelectedEmails([]);
  };

  const toggleBulkSelect = (emailId) => {
    setSelectedEmails(prev =>
      prev.includes(emailId) ? prev.filter(id => id !== emailId) : [...prev, emailId]
    );
  };

  const addAttachment = async () => {
    try {
      setIsUploading(true);
      const result = await DocumentPicker.getDocumentAsync({ type: '*/*', copyToCacheDirectory: true, multiple: true });
      if (!result.canceled && result.assets) {
        const newAtts = result.assets.map(a => ({
          name: a.name, uri: a.uri, type: a.mimeType || 'application/octet-stream',
          size: a.size ? `${(a.size / 1024).toFixed(0)} KB` : '?',
        }));
        setEmailForm(p => ({ ...p, attachments: [...p.attachments, ...newAtts] }));
      }
    } catch (_) {
      Alert.alert('Error', 'Failed to attach file.');
    } finally {
      setIsUploading(false);
    }
  };

  const applyTemplate = (tpl) => {
    setEmailForm(p => ({ ...p, subject: tpl.subject, body: tpl.body, category: tpl.category }));
    const updated = templates.map(t =>
      t.id === tpl.id ? { ...t, usageCount: (t.usageCount || 0) + 1, lastUsed: new Date().toISOString().split('T')[0] } : t
    );
    setTemplates(updated);
    saveTemplates(updated);
    setShowTemplateModal(false);
  };

  const replyToEmail = (email) => {
    setEmailForm({
      ...defaultForm,
      to: email.from,
      subject: email.subject.startsWith('Re: ') ? email.subject : `Re: ${email.subject}`,
      body: `\n\n─── Original message from ${email.fromName || email.from} on ${new Date(email.timestamp).toLocaleString()} ───\n${email.body}`,
    });
    setShowEmailDetail(false);
    setShowCompose(true);
  };

  const forwardEmail = (email) => {
    setEmailForm({
      ...defaultForm,
      subject: email.subject.startsWith('Fwd: ') ? email.subject : `Fwd: ${email.subject}`,
      body: `\n\n─── Forwarded from ${email.fromName || email.from} on ${new Date(email.timestamp).toLocaleString()} ───\n${email.body}`,
      attachments: [...(email.attachments || [])],
    });
    setShowEmailDetail(false);
    setShowCompose(true);
  };

  const resetForm = () => {
    setEmailForm(defaultForm);
    setSelectedEmail(null);
  };

  // ─── AI Compose ─────────────────────────────────────────────────────────────
  const generateAIBody = async () => {
    if (!aiPrompt.trim()) { Alert.alert('Enter a prompt', 'Describe what you want to write.'); return; }
    setIsAILoading(true);
    const result = await askAICompose(aiPrompt, `Recipient: ${emailForm.to}, Category: ${emailForm.category}`);
    if (result) {
      setEmailForm(p => ({ ...p, body: result }));
      setShowAIModal(false);
      setAiPrompt('');
    } else {
      Alert.alert('AI Unavailable', 'Could not generate email. Try again later.');
    }
    setIsAILoading(false);
  };

  // ─── Filtering ───────────────────────────────────────────────────────────────
  const getTabEmails = useCallback(() => {
    let list = [];
    switch (activeTab) {
      case 'inbox':     list = emails.filter(e => e.folder === 'inbox' || e.type === 'received' && e.folder !== 'trash' && e.folder !== 'spam'); break;
      case 'sent':      list = emails.filter(e => e.folder === 'sent' || e.type === 'sent' && e.folder !== 'trash'); break;
      case 'drafts':    return [...drafts];
      case 'starred':   list = [...emails, ...drafts].filter(e => e.starred); break;
      case 'scheduled': list = emails.filter(e => e.folder === 'scheduled' || e.type === 'scheduled'); break;
      case 'spam':      list = emails.filter(e => e.folder === 'spam'); break;
      case 'trash':     list = emails.filter(e => e.folder === 'trash'); break;
      case 'templates': return templates;
      default:          list = emails;
    }
    // Apply filters
    if (activeFilters.priority !== 'all') list = list.filter(e => e.priority === activeFilters.priority);
    if (activeFilters.category !== 'all') list = list.filter(e => e.category === activeFilters.category);
    if (activeFilters.read === 'unread')   list = list.filter(e => !e.read);
    if (activeFilters.read === 'read')     list = list.filter(e => e.read);
    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e =>
        e.subject?.toLowerCase().includes(q) ||
        e.body?.toLowerCase().includes(q) ||
        e.fromName?.toLowerCase().includes(q) ||
        e.to?.toLowerCase().includes(q) ||
        e.from?.toLowerCase().includes(q)
      );
    }
    // Sort
    if (sortBy === 'newest')   list = [...list].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    if (sortBy === 'oldest')   list = [...list].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    if (sortBy === 'priority') list = [...list].sort((a, b) => ['urgent','high','normal','low'].indexOf(a.priority) - ['urgent','high','normal','low'].indexOf(b.priority));
    return list;
  }, [activeTab, emails, drafts, templates, searchQuery, activeFilters, sortBy]);

  // ─── Badge counts ────────────────────────────────────────────────────────────
  const badgeCount = useCallback((tab) => {
    switch (tab) {
      case 'inbox':     return emails.filter(e => (e.folder === 'inbox' || e.type === 'received') && !e.read && e.folder !== 'trash').length;
      case 'drafts':    return drafts.length;
      case 'scheduled': return emails.filter(e => e.folder === 'scheduled').length;
      case 'spam':      return emails.filter(e => e.folder === 'spam').length;
      case 'trash':     return emails.filter(e => e.folder === 'trash').length;
      case 'templates': return templates.length;
      default:          return 0;
    }
  }, [emails, drafts, templates]);

  // ─── Analytics data ──────────────────────────────────────────────────────────
  const analytics = useMemo(() => {
    const sent     = emails.filter(e => e.type === 'sent').length;
    const received = emails.filter(e => e.type === 'received').length;
    const replied  = emails.filter(e => e.subject?.startsWith('Re:')).length;
    const avgAtts  = emails.reduce((s, e) => s + (e.attachments?.length || 0), 0) / Math.max(emails.length, 1);
    return {
      total: emails.length, sent, received, replied,
      unread: emails.filter(e => !e.read).length,
      starred: emails.filter(e => e.starred).length,
      scheduled: emails.filter(e => e.folder === 'scheduled').length,
      replyRate: received > 0 ? Math.round((replied / received) * 100) : 0,
      avgAttachments: avgAtts.toFixed(1),
      byCategory: CATEGORIES.map(c => ({
        ...c, count: emails.filter(e => e.category === c.key).length,
      })),
      byPriority: PRIORITIES.map(p => ({
        ...p, count: emails.filter(e => e.priority === p.key).length,
      })),
    };
  }, [emails]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const fmtDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts), now = new Date();
    const diff = (now - d) / 3600000;
    if (diff < 1) return `${Math.round(diff * 60)}m ago`;
    if (diff < 24) return `${Math.round(diff)}h ago`;
    if (diff < 168) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getPriorityColor = (p) => PRIORITIES.find(x => x.key === p)?.color || BRAND.sub;
  const getCategoryColor = (c) => CATEGORIES.find(x => x.key === c)?.color || BRAND.sub;
  const getFileIcon = (type) => {
    if (type?.includes('pdf'))   return 'file-pdf-box';
    if (type?.includes('image')) return 'file-image';
    if (type?.includes('zip'))   return 'folder-zip';
    if (type?.includes('word'))  return 'file-word';
    return 'file-document';
  };

  // ─── Render helpers ──────────────────────────────────────────────────────────
  const renderTabBar = () => (
    <View style={styles.tabBarWrapper}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tabBar}>
        {TABS.map(tab => {
          const count = badgeCount(tab.key);
          const active = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tabItem, active && { borderBottomColor: tab.color, borderBottomWidth: 2.5 }]}
              onPress={() => { setActiveTab(tab.key); setBulkMode(false); setSelectedEmails([]); }}
            >
              <MaterialCommunityIcons name={tab.icon} size={18} color={active ? tab.color : BRAND.sub} />
              <Text style={[styles.tabLabel, active && { color: tab.color, fontWeight: '700' }]}>{tab.label}</Text>
              {count > 0 && (
                <View style={[styles.tabBadge, { backgroundColor: active ? tab.color : BRAND.danger }]}>
                  <Text style={styles.tabBadgeText}>{count > 99 ? '99+' : count}</Text>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  const renderEmailCard = useCallback(({ item: email }) => {
    if (!email) return null; // guard against undefined items during FlatList transitions
    if (activeTab === 'templates') return renderTemplateCard(email);
    const isUnread  = !email.read && email.type !== 'sent' && email.folder !== 'sent';
    const isDraft   = activeTab === 'drafts';
    const inBulk    = selectedEmails.includes(email.id);
    const priColor  = getPriorityColor(email.priority);
    const catColor  = getCategoryColor(email.category);

    return (
      <TouchableOpacity
        style={[styles.emailCard, isUnread && styles.unreadCard, inBulk && styles.selectedCard]}
        onPress={() => {
          if (bulkMode) { toggleBulkSelect(email.id); return; }
          if (isDraft) {
            setSelectedEmail(email);
            setEmailForm(email);
            setShowCompose(true);
          } else {
            markAsRead(email.id);
            setSelectedEmail(email);
            setShowEmailDetail(true);
          }
        }}
        onLongPress={() => { setBulkMode(true); toggleBulkSelect(email.id); }}
      >
        {/* Left accent */}
        {isUnread && <View style={[styles.unreadAccent, { backgroundColor: BRAND.primary }]} />}

        {/* Bulk checkbox */}
        {bulkMode && (
          <TouchableOpacity style={styles.bulkCheckbox} onPress={() => toggleBulkSelect(email.id)}>
            <MaterialCommunityIcons name={inBulk ? 'checkbox-marked-circle' : 'checkbox-blank-circle-outline'} size={22} color={inBulk ? BRAND.primary : BRAND.sub} />
          </TouchableOpacity>
        )}

        <View style={styles.emailCardContent}>
          {/* Row 1: sender + time */}
          <View style={styles.emailRow}>
            <View style={styles.emailAvatar}>
              <Text style={styles.emailAvatarText}>
                {(isDraft ? (Array.isArray(email.to) ? email.to[0] : email.to) : (email.fromName || email.from || '?'))?.charAt(0)?.toUpperCase() || '?'}
              </Text>
            </View>
            <View style={{ flex: 1, marginLeft: 10 }}>
              <View style={styles.emailMeta}>
                <Text style={[styles.emailFrom, isUnread && styles.bold]} numberOfLines={1}>
                  {isDraft ? `Draft → ${Array.isArray(email.to) ? email.to.join(', ') : (email.to || 'No recipient')}` : (email.type === 'sent' ? `To: ${email.to || ''}` : email.fromName || email.from || '(Unknown)')}
                </Text>
                <Text style={styles.emailTime}>{fmtDate(email.timestamp)}</Text>
              </View>
              {/* Row 2: subject */}
              <Text style={[styles.emailSubject, isUnread && styles.bold]} numberOfLines={1}>
                {email.subject || '(No Subject)'}
              </Text>
              {/* Row 3: preview */}
              <Text style={styles.emailPreview} numberOfLines={1}>{email.body}</Text>
            </View>
            {/* Star */}
            <TouchableOpacity onPress={() => toggleStar(email.id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <MaterialCommunityIcons name={email.starred ? 'star' : 'star-outline'} size={18} color={email.starred ? BRAND.gold : '#cbd5e1'} />
            </TouchableOpacity>
          </View>

          {/* Row 4: tags + actions */}
          <View style={[styles.emailRow, { marginTop: 8 }]}>
            <View style={{ flexDirection: 'row', gap: 6, flex: 1 }}>
              {email.priority !== 'normal' && (
                <View style={[styles.badge, { backgroundColor: priColor + '20' }]}>
                  <Text style={[styles.badgeText, { color: priColor }]}>{email.priority}</Text>
                </View>
              )}
              {email.category && email.category !== 'general' && (
                <View style={[styles.badge, { backgroundColor: catColor + '20' }]}>
                  <Text style={[styles.badgeText, { color: catColor }]}>{email.category}</Text>
                </View>
              )}
              {email.attachments?.length > 0 && (
                <View style={styles.attBadge}>
                  <MaterialCommunityIcons name="paperclip" size={12} color={BRAND.sub} />
                  <Text style={styles.attBadgeText}>{email.attachments.length}</Text>
                </View>
              )}
              {email.folder === 'scheduled' && (
                <View style={[styles.badge, { backgroundColor: BRAND.purple + '20' }]}>
                  <MaterialCommunityIcons name="clock-outline" size={11} color={BRAND.purple} />
                  <Text style={[styles.badgeText, { color: BRAND.purple, marginLeft: 3 }]}>Scheduled</Text>
                </View>
              )}
            </View>
            {!bulkMode && !isDraft && (
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity onPress={() => replyToEmail(email)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <MaterialCommunityIcons name="reply" size={16} color={BRAND.sub} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => deleteEmail(email.id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <MaterialCommunityIcons name="delete-outline" size={16} color={BRAND.sub} />
                </TouchableOpacity>
              </View>
            )}
            {isDraft && (
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity onPress={() => deleteDraft(email.id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <MaterialCommunityIcons name="delete-outline" size={16} color={BRAND.danger} />
                </TouchableOpacity>
              </View>
            )}
            {activeTab === 'trash' && !bulkMode && (
              <TouchableOpacity onPress={() => restoreEmail(email.id)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <MaterialCommunityIcons name="restore" size={16} color={BRAND.success} />
              </TouchableOpacity>
            )}
          </View>
        </View>
      </TouchableOpacity>
    );
  }, [activeTab, bulkMode, selectedEmails, toggleStar, replyToEmail, deleteEmail,
      deleteDraft, restoreEmail, markAsRead, setBulkMode, toggleBulkSelect,
      setSelectedEmail, setEmailForm, setShowCompose, setShowEmailDetail, fmtDate]);

  const renderTemplateCard = (tpl) => (
    <TouchableOpacity
      style={styles.templateCard}
      onPress={() => { applyTemplate(tpl); setActiveTab('inbox'); setShowCompose(true); }}
    >
      <View style={styles.tplHeader}>
        <View style={{ flex: 1 }}>
          <Text style={styles.tplName}>{tpl.name}</Text>
          <Text style={styles.tplSubject} numberOfLines={1}>{tpl.subject}</Text>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 4 }}>
          <View style={[styles.badge, { backgroundColor: getCategoryColor(tpl.category) + '20' }]}>
            <Text style={[styles.badgeText, { color: getCategoryColor(tpl.category) }]}>{tpl.category}</Text>
          </View>
          {tpl.favourite && <MaterialCommunityIcons name="star" size={14} color={BRAND.gold} />}
        </View>
      </View>
      <Text style={styles.tplPreview} numberOfLines={2}>{tpl.body}</Text>
      <View style={styles.tplFooter}>
        <View style={{ flexDirection: 'row', gap: 6, flex: 1, flexWrap: 'wrap' }}>
          {tpl.tags?.slice(0, 3).map(tag => (
            <View key={tag} style={styles.tagChip}><Text style={styles.tagText}>#{tag}</Text></View>
          ))}
        </View>
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <MaterialCommunityIcons name="send-check" size={13} color={BRAND.sub} />
            <Text style={styles.tplMeta}>{tpl.usageCount} uses</Text>
          </View>
          <Text style={styles.tplMeta}>{tpl.lastUsed}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderAnalytics = () => (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
      <Text style={styles.sectionTitle}>Email Analytics</Text>
      {/* Stats grid */}
      <View style={styles.statsGrid}>
        {[
          { label: 'Total', value: analytics.total,    icon: 'email',              color: BRAND.primary },
          { label: 'Sent',  value: analytics.sent,     icon: 'send',               color: BRAND.success },
          { label: 'Received', value: analytics.received, icon: 'inbox',           color: BRAND.blue },
          { label: 'Unread',value: analytics.unread,   icon: 'email-alert',        color: BRAND.danger },
          { label: 'Starred', value: analytics.starred,icon: 'star',               color: BRAND.gold },
          { label: 'Scheduled', value: analytics.scheduled, icon: 'clock-outline', color: BRAND.purple },
          { label: 'Reply Rate', value: `${analytics.replyRate}%`, icon: 'reply', color: BRAND.teal },
          { label: 'Avg Attachments', value: analytics.avgAttachments, icon: 'paperclip', color: BRAND.orange },
        ].map(stat => (
          <View key={stat.label} style={[styles.statCard, { borderTopColor: stat.color }]}>
            <MaterialCommunityIcons name={stat.icon} size={22} color={stat.color} />
            <Text style={styles.statValue}>{stat.value}</Text>
            <Text style={styles.statLabel}>{stat.label}</Text>
          </View>
        ))}
      </View>
      {/* By category */}
      <Text style={styles.sectionTitle}>By Category</Text>
      {analytics.byCategory.filter(c => c.count > 0).map(c => (
        <View key={c.key} style={styles.analyticsRow}>
          <View style={[styles.analyticsBar, { backgroundColor: c.color + '20' }]}>
            <View style={[styles.analyticsFill, { backgroundColor: c.color, width: `${Math.min(100, (c.count / analytics.total) * 100)}%` }]} />
          </View>
          <Text style={[styles.analyticsLabel, { color: c.color }]}>{c.label}</Text>
          <Text style={styles.analyticsCount}>{c.count}</Text>
        </View>
      ))}
      {/* By priority */}
      <Text style={styles.sectionTitle}>By Priority</Text>
      {analytics.byPriority.filter(p => p.count > 0).map(p => (
        <View key={p.key} style={styles.analyticsRow}>
          <View style={[styles.analyticsBar, { backgroundColor: p.color + '20' }]}>
            <View style={[styles.analyticsFill, { backgroundColor: p.color, width: `${Math.min(100, (p.count / analytics.total) * 100)}%` }]} />
          </View>
          <Text style={[styles.analyticsLabel, { color: p.color }]}>{p.label}</Text>
          <Text style={styles.analyticsCount}>{p.count}</Text>
        </View>
      ))}
    </ScrollView>
  );

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name={TABS.find(t => t.key === activeTab)?.icon || 'email-outline'} size={56} color="#cbd5e1" />
      <Text style={styles.emptyTitle}>No emails here</Text>
      <Text style={styles.emptyText}>
        <Text>{activeTab === 'inbox' && 'Your inbox is empty. Fresh start!'}</Text>
        <Text>{activeTab === 'sent' && 'No sent emails yet.'}</Text>
        <Text>{activeTab === 'drafts' && 'No drafts saved.'}</Text>
        <Text>{activeTab === 'starred' && 'Star emails to find them here.'}</Text>
        <Text>{activeTab === 'scheduled' && 'No scheduled emails.'}</Text>
        <Text>{activeTab === 'spam' && 'No spam. Good news!'}</Text>
        <Text>{activeTab === 'trash' && 'Trash is empty.'}</Text>
        <Text>{activeTab === 'templates' && 'No templates yet.'}</Text>
      </Text>
      <TouchableOpacity style={styles.emptyBtn} onPress={() => { resetForm(); setShowCompose(true); }}>
        <MaterialCommunityIcons name="pencil" size={16} color={BRAND.white} />
        <Text style={styles.emptyBtnText}>Compose Email</Text>
      </TouchableOpacity>
    </View>
  );

  // ─── Compose Modal ───────────────────────────────────────────────────────────
  const renderComposeModal = () => (
    <Modal visible={showCompose} animationType="slide" presentationStyle="fullScreen" onRequestClose={() => setShowCompose(false)}>
      <SafeAreaView style={styles.modalBg}>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          {/* Header */}
          <LinearGradient colors={[BRAND.primary, BRAND.teal]} style={styles.composeHeader}>
            <TouchableOpacity onPress={() => { Alert.alert('Close', 'Save as draft or discard?', [
              { text: 'Discard',    style: 'destructive', onPress: () => { resetForm(); setShowCompose(false); }},
              { text: 'Save Draft', onPress: saveDraft },
              { text: 'Cancel',    style: 'cancel' },
            ]); }}>
              <Ionicons name="close" size={24} color={BRAND.white} />
            </TouchableOpacity>
            <Text style={styles.composeTitle}>{selectedEmail?.id?.startsWith('draft_') ? 'Edit Draft' : 'Compose Email'}</Text>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <TouchableOpacity onPress={() => setShowAIModal(true)}>
                <MaterialCommunityIcons name="robot-outline" size={24} color={BRAND.white} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setShowTemplateModal(true)}>
                <MaterialCommunityIcons name="file-document-multiple" size={24} color={BRAND.white} />
              </TouchableOpacity>
            </View>
          </LinearGradient>

          <ScrollView style={styles.composeBody} keyboardShouldPersistTaps="handled">
            {/* Recipients */}
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>To</Text>
              <TextInput
                style={styles.fieldInput}
                value={emailForm.to}
                onChangeText={t => setEmailForm(p => ({ ...p, to: t }))}
                placeholder="Recipient email"
                keyboardType="email-address"
                autoCapitalize="none"
              />
              <TouchableOpacity onPress={() => setShowContactPicker(true)}>
                <MaterialCommunityIcons name="account-search" size={20} color={BRAND.primary} />
              </TouchableOpacity>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>CC</Text>
              <TextInput
                style={styles.fieldInput}
                value={emailForm.cc}
                onChangeText={t => setEmailForm(p => ({ ...p, cc: t }))}
                placeholder="CC"
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>BCC</Text>
              <TextInput
                style={styles.fieldInput}
                value={emailForm.bcc}
                onChangeText={t => setEmailForm(p => ({ ...p, bcc: t }))}
                placeholder="BCC"
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Subject</Text>
              <TextInput
                style={styles.fieldInput}
                value={emailForm.subject}
                onChangeText={t => setEmailForm(p => ({ ...p, subject: t }))}
                placeholder="Subject"
              />
            </View>

            {/* Priority & Category */}
            <View style={styles.composeSection}>
              <Text style={styles.composeSectionLabel}>Priority</Text>
              <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                {PRIORITIES.map(p => (
                  <TouchableOpacity
                    key={p.key}
                    style={[styles.chipBtn, emailForm.priority === p.key && { backgroundColor: p.color, borderColor: p.color }]}
                    onPress={() => setEmailForm(prev => ({ ...prev, priority: p.key }))}
                  >
                    <Text style={[styles.chipText, emailForm.priority === p.key && { color: BRAND.white }]}>{p.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <View style={styles.composeSection}>
              <Text style={styles.composeSectionLabel}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  {CATEGORIES.map(c => (
                    <TouchableOpacity
                      key={c.key}
                      style={[styles.chipBtn, emailForm.category === c.key && { backgroundColor: c.color, borderColor: c.color }]}
                      onPress={() => setEmailForm(prev => ({ ...prev, category: c.key }))}
                    >
                      <Text style={[styles.chipText, emailForm.category === c.key && { color: BRAND.white }]}>{c.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Body */}
            <View style={styles.composeSection}>
              <TextInput
                style={styles.bodyInput}
                value={emailForm.body}
                onChangeText={t => setEmailForm(p => ({ ...p, body: t }))}
                placeholder="Write your message..."
                multiline
                textAlignVertical="top"
              />
            </View>

            {/* Attachments */}
            <View style={styles.composeSection}>
              <TouchableOpacity style={styles.attBtn} onPress={addAttachment} disabled={isUploading}>
                {isUploading ? <ActivityIndicator size={16} color={BRAND.primary} /> : <MaterialCommunityIcons name="paperclip" size={18} color={BRAND.primary} />}
                <Text style={styles.attBtnText}>{isUploading ? 'Adding...' : 'Add Attachment'}</Text>
              </TouchableOpacity>
              {emailForm.attachments.map((att, i) => (
                <View key={i} style={styles.attItem}>
                  <MaterialCommunityIcons name={getFileIcon(att.type)} size={22} color={BRAND.primary} />
                  <View style={{ flex: 1, marginLeft: 10 }}>
                    <Text style={styles.attName} numberOfLines={1}>{att.name}</Text>
                    <Text style={styles.attSize}>{att.size}</Text>
                  </View>
                  <TouchableOpacity onPress={() => setEmailForm(p => ({ ...p, attachments: p.attachments.filter((_, j) => j !== i) }))}>
                    <MaterialCommunityIcons name="close-circle" size={20} color={BRAND.danger} />
                  </TouchableOpacity>
                </View>
              ))}
            </View>

            {/* Options */}
            <View style={styles.composeSection}>
              <Text style={styles.composeSectionLabel}>Options</Text>
              {[
                { key: 'signature',       label: 'Include Signature' },
                { key: 'readReceipt',     label: 'Request Read Receipt' },
                { key: 'deliveryReceipt', label: 'Delivery Receipt' },
                { key: 'trackOpens',      label: 'Track Opens' },
              ].map(opt => (
                <View key={opt.key} style={styles.optRow}>
                  <Text style={styles.optLabel}>{opt.label}</Text>
                  <Switch
                    value={!!emailForm[opt.key]}
                    onValueChange={v => setEmailForm(p => ({ ...p, [opt.key]: v }))}
                    trackColor={{ true: BRAND.primary }}
                    thumbColor={BRAND.white}
                  />
                </View>
              ))}
              {emailForm.signature && (
                <View style={{ marginTop: 8 }}>
                  <Text style={styles.composeSectionLabel}>Signature</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      {SIGNATURES.map(sig => (
                        <TouchableOpacity
                          key={sig.id}
                          style={[styles.chipBtn, emailForm.signatureId === sig.id && { backgroundColor: BRAND.primary, borderColor: BRAND.primary }]}
                          onPress={() => setEmailForm(p => ({ ...p, signatureId: sig.id }))}
                        >
                          <Text style={[styles.chipText, emailForm.signatureId === sig.id && { color: BRAND.white }]}>{sig.name}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </ScrollView>
                </View>
              )}
            </View>

            {/* Actions */}
            <View style={styles.composeActions}>
              <TouchableOpacity style={styles.draftBtn} onPress={saveDraft}>
                <MaterialCommunityIcons name="content-save" size={18} color={BRAND.sub} />
                <Text style={styles.draftBtnText}>Save Draft</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.schedBtn} onPress={() => setShowSchedule(true)}>
                <MaterialCommunityIcons name="clock-outline" size={18} color={BRAND.purple} />
                <Text style={styles.schedBtnText}>Schedule</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.sendBtn} onPress={sendEmail} disabled={isSubmitting}>
                {isSubmitting ? <ActivityIndicator size={18} color={BRAND.white} /> : <MaterialCommunityIcons name="send" size={18} color={BRAND.white} />}
                <Text style={styles.sendBtnText}>{isSubmitting ? 'Sending...' : 'Send'}</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // ─── Email Detail Modal ──────────────────────────────────────────────────────
  const renderDetailModal = () => (
    <Modal visible={showEmailDetail} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowEmailDetail(false)}>
      <SafeAreaView style={styles.modalBg}>
        {selectedEmail && (
          <>
            <View style={styles.detailHeader}>
              <TouchableOpacity onPress={() => setShowEmailDetail(false)}>
                <Ionicons name="close" size={24} color={BRAND.dark} />
              </TouchableOpacity>
              <View style={{ flexDirection: 'row', gap: 16 }}>
                <TouchableOpacity onPress={() => toggleStar(selectedEmail.id)}>
                  <MaterialCommunityIcons name={selectedEmail.starred ? 'star' : 'star-outline'} size={22} color={selectedEmail.starred ? BRAND.gold : BRAND.sub} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => { setShowEmailDetail(false); deleteEmail(selectedEmail.id); }}>
                  <MaterialCommunityIcons name="delete-outline" size={22} color={BRAND.danger} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => Share.share({ message: `${selectedEmail.subject}\n\n${selectedEmail.body}` })}>
                  <MaterialCommunityIcons name="share-variant" size={22} color={BRAND.primary} />
                </TouchableOpacity>
              </View>
            </View>

            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
              {/* Subject */}
              <Text style={styles.detailSubject}>{selectedEmail.subject || '(No Subject)'}</Text>

              {/* Meta */}
              <View style={styles.detailMeta}>
                {[
                  { label: 'From', value: `${selectedEmail.fromName || ''} <${selectedEmail.from}>` },
                  { label: 'To',   value: selectedEmail.to },
                  selectedEmail.cc ? { label: 'CC', value: selectedEmail.cc } : null,
                  { label: 'Date', value: new Date(selectedEmail.timestamp).toLocaleString() },
                ].filter(Boolean).map(row => (
                  <View key={row.label} style={styles.detailMetaRow}>
                    <Text style={styles.detailMetaLabel}>{row.label}:</Text>
                    <Text style={styles.detailMetaValue} numberOfLines={2}>{row.value}</Text>
                  </View>
                ))}
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  {selectedEmail.priority && selectedEmail.priority !== 'normal' && (
                    <View style={[styles.badge, { backgroundColor: getPriorityColor(selectedEmail.priority) + '20' }]}>
                      <Text style={[styles.badgeText, { color: getPriorityColor(selectedEmail.priority) }]}>{selectedEmail.priority} priority</Text>
                    </View>
                  )}
                  {selectedEmail.category && selectedEmail.category !== 'general' && (
                    <View style={[styles.badge, { backgroundColor: getCategoryColor(selectedEmail.category) + '20' }]}>
                      <Text style={[styles.badgeText, { color: getCategoryColor(selectedEmail.category) }]}>{selectedEmail.category}</Text>
                    </View>
                  )}
                </View>
              </View>

              {/* Attachments */}
              {selectedEmail.attachments?.length > 0 && (
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Attachments ({selectedEmail.attachments.length})</Text>
                  {selectedEmail.attachments.map((att, i) => (
                    <View key={i} style={styles.attDetailItem}>
                      <MaterialCommunityIcons name={getFileIcon(att.type)} size={28} color={BRAND.primary} />
                      <View style={{ flex: 1, marginLeft: 12 }}>
                        <Text style={styles.attName}>{att.name}</Text>
                        <Text style={styles.attSize}>{att.size}</Text>
                      </View>
                      <TouchableOpacity style={styles.downloadBtn}>
                        <MaterialCommunityIcons name="download" size={20} color={BRAND.primary} />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}

              {/* Body */}
              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Message</Text>
                <View style={styles.emailBody}>
                  <Text style={styles.emailBodyText}>{selectedEmail.body}</Text>
                </View>
              </View>

              {/* Action buttons */}
              <View style={styles.detailActions}>
                <TouchableOpacity style={[styles.detailActionBtn, { borderColor: BRAND.primary }]} onPress={() => replyToEmail(selectedEmail)}>
                  <MaterialCommunityIcons name="reply" size={18} color={BRAND.primary} />
                  <Text style={[styles.detailActionText, { color: BRAND.primary }]}>Reply</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.detailActionBtn, { borderColor: BRAND.teal }]} onPress={() => forwardEmail(selectedEmail)}>
                  <MaterialCommunityIcons name="share" size={18} color={BRAND.teal} />
                  <Text style={[styles.detailActionText, { color: BRAND.teal }]}>Forward</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.detailActionBtn, { borderColor: BRAND.warning }]}
                  onPress={() => {
                    const match = contacts.find(c => c.email === selectedEmail.from);
                    if (match?.phone) Linking.openURL(`tel:${match.phone}`);
                    else Alert.alert('No phone', 'No phone on record for this sender.');
                  }}
                >
                  <Ionicons name="call" size={18} color={BRAND.warning} />
                  <Text style={[styles.detailActionText, { color: BRAND.warning }]}>Call</Text>
                </TouchableOpacity>
              </View>

              {activeTab === 'trash' && (
                <TouchableOpacity style={[styles.detailActionBtn, { borderColor: BRAND.success, marginTop: 12 }]} onPress={() => { restoreEmail(selectedEmail.id); setShowEmailDetail(false); }}>
                  <MaterialCommunityIcons name="restore" size={18} color={BRAND.success} />
                  <Text style={[styles.detailActionText, { color: BRAND.success }]}>Restore</Text>
                </TouchableOpacity>
              )}
            </ScrollView>
          </>
        )}
      </SafeAreaView>
    </Modal>
  );

  // ─── AI Modal ────────────────────────────────────────────────────────────────
  const renderAIModal = () => (
    <Modal visible={showAIModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowAIModal(false)}>
      <SafeAreaView style={styles.modalBg}>
        <View style={styles.sheetHeader}>
          <Text style={styles.sheetTitle}>AI Email Composer</Text>
          <TouchableOpacity onPress={() => setShowAIModal(false)}>
            <Ionicons name="close" size={24} color={BRAND.dark} />
          </TouchableOpacity>
        </View>
        <View style={{ padding: 20 }}>
          <Text style={styles.aiLabel}>Describe what you want to write</Text>
          <TextInput
            style={styles.aiInput}
            value={aiPrompt}
            onChangeText={setAiPrompt}
            placeholder="e.g. Professional follow-up on tender submission, friendly but firm..."
            multiline
            numberOfLines={5}
            textAlignVertical="top"
          />
          <View style={{ flexDirection: 'row', gap: 10, marginTop: 12 }}>
            {['Follow-up email', 'Quote proposal', 'Meeting request', 'Invoice reminder'].map(s => (
              <TouchableOpacity key={s} style={styles.aiSuggestion} onPress={() => setAiPrompt(s)}>
                <Text style={styles.aiSuggestionText}>{s}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity style={[styles.aiGenBtn, isAILoading && { opacity: 0.6 }]} onPress={generateAIBody} disabled={isAILoading}>
            {isAILoading ? <ActivityIndicator size={18} color={BRAND.white} /> : <MaterialCommunityIcons name="robot" size={18} color={BRAND.white} />}
            <Text style={styles.aiGenBtnText}>{isAILoading ? 'Generating...' : 'Generate Email'}</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </Modal>
  );

  // ─── Template Modal ──────────────────────────────────────────────────────────
  const renderTemplateModal = () => (
    <Modal visible={showTemplateModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowTemplateModal(false)}>
      <SafeAreaView style={styles.modalBg}>
        <View style={styles.sheetHeader}>
          <Text style={styles.sheetTitle}>Select Template</Text>
          <TouchableOpacity onPress={() => setShowTemplateModal(false)}>
            <Ionicons name="close" size={24} color={BRAND.dark} />
          </TouchableOpacity>
        </View>
        <FlatList
          data={templates}
          keyExtractor={t => t.id}
          contentContainerStyle={{ padding: 16 }}
          renderItem={({ item: tpl }) => (
            <TouchableOpacity style={styles.tplSelectItem} onPress={() => applyTemplate(tpl)}>
              <View style={{ flex: 1 }}>
                <Text style={styles.tplSelectName}>{tpl.name}</Text>
                <Text style={styles.tplSelectSubject} numberOfLines={1}>{tpl.subject}</Text>
              </View>
              <View style={[styles.badge, { backgroundColor: getCategoryColor(tpl.category) + '20' }]}>
                <Text style={[styles.badgeText, { color: getCategoryColor(tpl.category) }]}>{tpl.category}</Text>
              </View>
            </TouchableOpacity>
          )}
        />
      </SafeAreaView>
    </Modal>
  );

  // ─── Contact Picker Modal ────────────────────────────────────────────────────
  const renderContactPicker = () => (
    <Modal visible={showContactPicker} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowContactPicker(false)}>
      <SafeAreaView style={styles.modalBg}>
        <View style={styles.sheetHeader}>
          <Text style={styles.sheetTitle}>Select Contact</Text>
          <TouchableOpacity onPress={() => setShowContactPicker(false)}>
            <Ionicons name="close" size={24} color={BRAND.dark} />
          </TouchableOpacity>
        </View>
        <FlatList
          data={contacts}
          keyExtractor={c => c.id}
          contentContainerStyle={{ padding: 16 }}
          renderItem={({ item: c }) => (
            <TouchableOpacity style={styles.contactItem} onPress={() => {
              setEmailForm(p => ({ ...p, to: c.email }));
              setShowContactPicker(false);
            }}>
              <View style={styles.contactAvatar}>
                <Text style={styles.contactInitial}>{c.name[0]}</Text>
              </View>
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={styles.contactName}>{c.name}</Text>
                <Text style={styles.contactEmail}>{c.email}</Text>
                <Text style={styles.contactCompany}>{c.company}</Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={20} color={BRAND.sub} />
            </TouchableOpacity>
          )}
        />
      </SafeAreaView>
    </Modal>
  );

  // ─── Filters Modal ───────────────────────────────────────────────────────────
  const renderFiltersModal = () => (
    <Modal visible={showFilters} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowFilters(false)}>
      <SafeAreaView style={styles.modalBg}>
        <View style={styles.sheetHeader}>
          <Text style={styles.sheetTitle}>Filters & Sort</Text>
          <TouchableOpacity onPress={() => { setActiveFilters({ priority: 'all', category: 'all', read: 'all' }); setSortBy('newest'); }}>
            <Text style={{ color: BRAND.danger, fontWeight: '600' }}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setShowFilters(false)}>
            <Ionicons name="close" size={24} color={BRAND.dark} />
          </TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ padding: 20 }}>
          <Text style={styles.filterSectionTitle}>Sort By</Text>
          {[
            { key: 'newest',   label: 'Newest First',   icon: 'sort-calendar-descending' },
            { key: 'oldest',   label: 'Oldest First',   icon: 'sort-calendar-ascending' },
            { key: 'priority', label: 'Priority',       icon: 'sort-descending' },
          ].map(s => (
            <TouchableOpacity key={s.key} style={[styles.filterItem, sortBy === s.key && styles.filterItemActive]} onPress={() => setSortBy(s.key)}>
              <MaterialCommunityIcons name={s.icon} size={20} color={sortBy === s.key ? BRAND.primary : BRAND.sub} />
              <Text style={[styles.filterItemText, sortBy === s.key && { color: BRAND.primary }]}>{s.label}</Text>
              {sortBy === s.key && <MaterialCommunityIcons name="check" size={18} color={BRAND.primary} />}
            </TouchableOpacity>
          ))}

          <Text style={styles.filterSectionTitle}>Read Status</Text>
          {[{ key: 'all', label: 'All' }, { key: 'unread', label: 'Unread' }, { key: 'read', label: 'Read' }].map(r => (
            <TouchableOpacity key={r.key} style={[styles.filterItem, activeFilters.read === r.key && styles.filterItemActive]} onPress={() => setActiveFilters(p => ({ ...p, read: r.key }))}>
              <Text style={[styles.filterItemText, activeFilters.read === r.key && { color: BRAND.primary }]}>{r.label}</Text>
              {activeFilters.read === r.key && <MaterialCommunityIcons name="check" size={18} color={BRAND.primary} />}
            </TouchableOpacity>
          ))}

          <Text style={styles.filterSectionTitle}>Priority</Text>
          {[{ key: 'all', label: 'All Priorities' }, ...PRIORITIES].map(p => (
            <TouchableOpacity key={p.key} style={[styles.filterItem, activeFilters.priority === p.key && styles.filterItemActive]} onPress={() => setActiveFilters(prev => ({ ...prev, priority: p.key }))}>
              <Text style={[styles.filterItemText, { color: activeFilters.priority === p.key ? BRAND.primary : BRAND.dark }]}>{p.label}</Text>
              {activeFilters.priority === p.key && <MaterialCommunityIcons name="check" size={18} color={BRAND.primary} />}
            </TouchableOpacity>
          ))}

          <Text style={styles.filterSectionTitle}>Category</Text>
          {[{ key: 'all', label: 'All Categories' }, ...CATEGORIES].map(c => (
            <TouchableOpacity key={c.key} style={[styles.filterItem, activeFilters.category === c.key && styles.filterItemActive]} onPress={() => setActiveFilters(prev => ({ ...prev, category: c.key }))}>
              <Text style={[styles.filterItemText, { color: activeFilters.category === c.key ? BRAND.primary : BRAND.dark }]}>{c.label}</Text>
              {activeFilters.category === c.key && <MaterialCommunityIcons name="check" size={18} color={BRAND.primary} />}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );

  // ─── Main Render ─────────────────────────────────────────────────────────────
  const displayList = useMemo(() => getTabEmails(), [getTabEmails]);
  const unreadCount = emails.filter(e => (e.folder === 'inbox' || e.type === 'received') && !e.read && e.folder !== 'trash').length;

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingView}>
          <ActivityIndicator size={40} color={BRAND.primary} />
          <Text style={styles.loadingText}>Loading emails…</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={BRAND.primary} />

      {/* ── Header ── */}
      <LinearGradient colors={[BRAND.primary, BRAND.teal]} style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
          <Ionicons name="arrow-back" size={24} color={BRAND.white} />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <MaterialCommunityIcons name="email" size={22} color={BRAND.white} />
          <View style={{ marginLeft: 10 }}>
            <Text style={styles.headerTitle}>Email Management</Text>
            {unreadCount > 0 && <Text style={styles.headerSub}>{unreadCount} unread</Text>}
          </View>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerBtn} onPress={() => setShowSearch(!showSearch)}>
            <MaterialCommunityIcons name="magnify" size={22} color={BRAND.white} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerBtn} onPress={() => setShowFilters(true)}>
            <MaterialCommunityIcons name="filter-variant" size={22} color={BRAND.white} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerBtn} onPress={() => { resetForm(); setShowCompose(true); }}>
            <MaterialCommunityIcons name="pencil-plus" size={22} color={BRAND.white} />
          </TouchableOpacity>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={{ width: 32, height: 32, borderRadius: 16, marginLeft: 6, resizeMode: 'cover' }} />
          ) : null}
        </View>
      </LinearGradient>

      {/* Search bar */}
      {showSearch && (
        <View style={styles.searchBar}>
          <MaterialCommunityIcons name="magnify" size={18} color={BRAND.sub} />
          <TextInput
            style={styles.searchInput}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search emails..."
            autoFocus
            returnKeyType="search"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <MaterialCommunityIcons name="close-circle" size={18} color={BRAND.sub} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Tab bar */}
      {renderTabBar()}

      {/* Bulk mode toolbar */}
      {bulkMode && (
        <View style={styles.bulkBar}>
          <TouchableOpacity onPress={() => { setBulkMode(false); setSelectedEmails([]); }}>
            <MaterialCommunityIcons name="close" size={22} color={BRAND.dark} />
          </TouchableOpacity>
          <Text style={styles.bulkCount}>{selectedEmails.length} selected</Text>
          <View style={{ flexDirection: 'row', gap: 16 }}>
            {[
              { action: 'read',  icon: 'email-open-outline', color: BRAND.primary },
              { action: 'star',  icon: 'star-outline',       color: BRAND.gold },
              { action: 'trash', icon: 'delete-outline',     color: BRAND.danger },
            ].map(a => (
              <TouchableOpacity key={a.action} onPress={() => bulkAction(a.action)}>
                <MaterialCommunityIcons name={a.icon} size={22} color={a.color} />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      {/* Content */}
      {activeTab === 'analytics' ? renderAnalytics() : (
        <FlatList
          data={displayList}
          keyExtractor={(item, index) => item?.id?.toString() ?? String(index)}
          renderItem={renderEmailCard}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[BRAND.primary]} />}
          ListEmptyComponent={renderEmptyState}
        />
      )}

      {/* Modals */}
      {showCompose        && renderComposeModal()}
      {showEmailDetail    && renderDetailModal()}
      {showAIModal        && renderAIModal()}
      {showTemplateModal  && renderTemplateModal()}
      {showContactPicker  && renderContactPicker()}
      {showFilters        && renderFiltersModal()}
      {renderContactPicker()}
      {renderFiltersModal()}

      {/* Scheduled send modal */}
      <Modal visible={showSchedule} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowSchedule(false)}>
        <SafeAreaView style={styles.modalBg}>
          <View style={styles.sheetHeader}>
            <Text style={styles.sheetTitle}>Schedule Send</Text>
            <TouchableOpacity onPress={() => setShowSchedule(false)}>
              <Ionicons name="close" size={24} color={BRAND.dark} />
            </TouchableOpacity>
          </View>
          <View style={{ padding: 20 }}>
            <Text style={{ color: BRAND.sub, marginBottom: 20, fontSize: 14 }}>
              Select a time to send your email
            </Text>
            {[
              { label: 'In 1 hour',      ms: 3600000 },
              { label: 'Tomorrow 9am',   ms: 24 * 3600000 },
              { label: 'This evening 6pm', ms: 12 * 3600000 },
              { label: 'Next Monday 9am', ms: 7 * 24 * 3600000 },
            ].map(opt => (
              <TouchableOpacity key={opt.label} style={styles.schedOption} onPress={() => {
                const time = new Date(Date.now() + opt.ms).toISOString();
                setEmailForm(p => ({ ...p, scheduledSend: time }));
                setShowSchedule(false);
                Alert.alert('Scheduled', `Email will be sent: ${new Date(time).toLocaleString()}`);
              }}>
                <MaterialCommunityIcons name="clock-outline" size={20} color={BRAND.purple} />
                <Text style={{ marginLeft: 12, fontSize: 16, color: BRAND.dark }}>{opt.label}</Text>
                <MaterialCommunityIcons name="chevron-right" size={20} color={BRAND.sub} style={{ marginLeft: 'auto' }} />
              </TouchableOpacity>
            ))}
            {emailForm.scheduledSend && (
              <TouchableOpacity style={[styles.schedOption, { backgroundColor: BRAND.danger + '15' }]} onPress={() => {
                setEmailForm(p => ({ ...p, scheduledSend: null }));
                setShowSchedule(false);
              }}>
                <MaterialCommunityIcons name="close-circle" size={20} color={BRAND.danger} />
                <Text style={{ marginLeft: 12, fontSize: 16, color: BRAND.danger }}>Clear Schedule</Text>
              </TouchableOpacity>
            )}
          </View>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container:      { flex: 1, backgroundColor: BRAND.bg },
  loadingView:    { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText:    { marginTop: 12, color: BRAND.sub, fontSize: 15 },
  header:         { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 14, paddingTop: Platform.OS === 'ios' ? 54 : 14 },
  backBtn:        { marginRight: 8, padding: 4 },
  headerCenter:   { flex: 1, flexDirection: 'row', alignItems: 'center' },
  headerTitle:    { fontSize: 17, fontWeight: '700', color: BRAND.white },
  headerSub:      { fontSize: 12, color: 'rgba(255,255,255,0.8)' },
  headerActions:  { flexDirection: 'row', gap: 4 },
  headerBtn:      { padding: 8 },
  searchBar:      { flexDirection: 'row', alignItems: 'center', backgroundColor: BRAND.white, paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  searchInput:    { flex: 1, marginHorizontal: 10, fontSize: 15, color: BRAND.dark },
  tabBarWrapper:  { backgroundColor: BRAND.white, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  tabBar:         { paddingHorizontal: 8 },
  tabItem:        { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 14, gap: 6 },
  tabLabel:       { fontSize: 13, color: BRAND.sub, fontWeight: '500' },
  tabBadge:       { minWidth: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 4 },
  tabBadgeText:   { fontSize: 11, color: BRAND.white, fontWeight: '700' },
  bulkBar:        { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 12, backgroundColor: '#eff6ff', borderBottomWidth: 1, borderBottomColor: BRAND.line },
  bulkCount:      { flex: 1, marginLeft: 12, fontSize: 14, fontWeight: '600', color: BRAND.dark },
  listContent:    { padding: 12, paddingBottom: 80 },
  emailCard:      { backgroundColor: BRAND.white, borderRadius: 12, marginBottom: 8, overflow: 'hidden', flexDirection: 'row', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 3, elevation: 2 },
  unreadCard:     { backgroundColor: BRAND.unread },
  selectedCard:   { borderWidth: 2, borderColor: BRAND.primary },
  unreadAccent:   { width: 4, backgroundColor: BRAND.primary },
  bulkCheckbox:   { justifyContent: 'center', paddingLeft: 12 },
  emailCardContent: { flex: 1, padding: 14 },
  emailRow:       { flexDirection: 'row', alignItems: 'center' },
  emailAvatar:    { width: 40, height: 40, borderRadius: 20, backgroundColor: BRAND.primary, justifyContent: 'center', alignItems: 'center' },
  emailAvatarText:{ color: BRAND.white, fontSize: 16, fontWeight: '700' },
  emailMeta:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 },
  emailFrom:      { flex: 1, fontSize: 14, color: BRAND.dark, fontWeight: '500', marginRight: 8 },
  emailTime:      { fontSize: 12, color: BRAND.sub },
  emailSubject:   { fontSize: 14, color: BRAND.dark, marginBottom: 3 },
  emailPreview:   { fontSize: 13, color: BRAND.sub },
  bold:           { fontWeight: '700' },
  badge:          { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  badgeText:      { fontSize: 11, fontWeight: '600' },
  attBadge:       { flexDirection: 'row', alignItems: 'center', gap: 3 },
  attBadgeText:   { fontSize: 11, color: BRAND.sub },
  // Template card
  templateCard:   { backgroundColor: BRAND.white, borderRadius: 12, padding: 16, marginBottom: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 3, elevation: 2 },
  tplHeader:      { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 },
  tplName:        { fontSize: 15, fontWeight: '700', color: BRAND.dark },
  tplSubject:     { fontSize: 13, color: BRAND.primary, marginTop: 2 },
  tplPreview:     { fontSize: 13, color: BRAND.sub, lineHeight: 19, marginBottom: 10 },
  tplFooter:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  tagChip:        { backgroundColor: '#f1f5f9', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  tagText:        { fontSize: 11, color: BRAND.sub },
  tplMeta:        { fontSize: 11, color: BRAND.sub },
  // Empty
  emptyState:     { alignItems: 'center', justifyContent: 'center', paddingVertical: 80 },
  emptyTitle:     { fontSize: 18, fontWeight: '700', color: BRAND.dark, marginTop: 16 },
  emptyText:      { fontSize: 14, color: BRAND.sub, marginTop: 8, marginBottom: 24, textAlign: 'center' },
  emptyBtn:       { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: BRAND.primary, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 24 },
  emptyBtnText:   { color: BRAND.white, fontWeight: '600' },
  // Analytics
  sectionTitle:   { fontSize: 16, fontWeight: '700', color: BRAND.dark, marginBottom: 12, marginTop: 8 },
  statsGrid:      { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 20 },
  statCard:       { width: (width - 52) / 2, backgroundColor: BRAND.white, borderRadius: 12, padding: 14, alignItems: 'center', borderTopWidth: 3, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 },
  statValue:      { fontSize: 24, fontWeight: '800', color: BRAND.dark, marginTop: 6 },
  statLabel:      { fontSize: 12, color: BRAND.sub, marginTop: 4, textAlign: 'center' },
  analyticsRow:   { flexDirection: 'row', alignItems: 'center', marginBottom: 10, gap: 10 },
  analyticsBar:   { flex: 1, height: 8, borderRadius: 4, overflow: 'hidden' },
  analyticsFill:  { height: '100%', borderRadius: 4, minWidth: 4 },
  analyticsLabel: { width: 80, fontSize: 12, fontWeight: '600' },
  analyticsCount: { width: 28, fontSize: 12, color: BRAND.sub, textAlign: 'right' },
  // Modal shared
  modalBg:        { flex: 1, backgroundColor: BRAND.bg },
  sheetHeader:    { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 16, backgroundColor: BRAND.white, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  sheetTitle:     { fontSize: 18, fontWeight: '700', color: BRAND.dark },
  // Compose
  composeHeader:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 14, paddingTop: Platform.OS === 'ios' ? 54 : 14 },
  composeTitle:   { fontSize: 16, fontWeight: '700', color: BRAND.white },
  composeBody:    { flex: 1, backgroundColor: BRAND.white },
  fieldRow:       { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  fieldLabel:     { width: 52, fontSize: 13, fontWeight: '600', color: BRAND.sub },
  fieldInput:     { flex: 1, fontSize: 15, color: BRAND.dark, marginHorizontal: 8 },
  composeSection: { paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  composeSectionLabel: { fontSize: 13, fontWeight: '600', color: BRAND.sub, marginBottom: 10 },
  chipBtn:        { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 20, borderWidth: 1, borderColor: BRAND.line, backgroundColor: BRAND.white },
  chipText:       { fontSize: 13, fontWeight: '500', color: BRAND.dark },
  bodyInput:      { minHeight: 200, fontSize: 15, color: BRAND.dark, textAlignVertical: 'top', lineHeight: 22 },
  attBtn:         { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, marginBottom: 8 },
  attBtnText:     { fontSize: 14, color: BRAND.primary, fontWeight: '600' },
  attItem:        { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 8 },
  attName:        { fontSize: 13, fontWeight: '600', color: BRAND.dark },
  attSize:        { fontSize: 12, color: BRAND.sub, marginTop: 2 },
  optRow:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 8 },
  optLabel:       { fontSize: 15, color: BRAND.dark },
  composeActions: { flexDirection: 'row', gap: 10, padding: 16, borderTopWidth: 1, borderTopColor: BRAND.line },
  draftBtn:       { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 14, borderRadius: 12, borderWidth: 1, borderColor: BRAND.line, backgroundColor: BRAND.bg },
  draftBtnText:   { fontSize: 14, fontWeight: '600', color: BRAND.sub },
  schedBtn:       { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 14, borderRadius: 12, borderWidth: 1, borderColor: BRAND.purple + '60', backgroundColor: BRAND.purple + '10' },
  schedBtnText:   { fontSize: 14, fontWeight: '600', color: BRAND.purple },
  sendBtn:        { flex: 2, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 14, borderRadius: 12, backgroundColor: BRAND.primary },
  sendBtnText:    { fontSize: 15, fontWeight: '700', color: BRAND.white },
  // Email detail
  detailHeader:   { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 14, backgroundColor: BRAND.white, borderBottomWidth: 1, borderBottomColor: BRAND.line },
  detailSubject:  { fontSize: 22, fontWeight: '700', color: BRAND.dark, marginBottom: 16, lineHeight: 30 },
  detailMeta:     { backgroundColor: '#f8fafc', borderRadius: 12, padding: 16, marginBottom: 20 },
  detailMetaRow:  { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 },
  detailMetaLabel:{ width: 48, fontSize: 13, fontWeight: '600', color: BRAND.sub },
  detailMetaValue:{ flex: 1, fontSize: 14, color: BRAND.dark },
  detailSection:  { marginBottom: 24 },
  detailSectionTitle: { fontSize: 15, fontWeight: '700', color: BRAND.dark, marginBottom: 10 },
  attDetailItem:  { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 10, padding: 12, marginBottom: 8 },
  downloadBtn:    { padding: 8 },
  emailBody:      { backgroundColor: '#f8fafc', borderRadius: 10, padding: 16 },
  emailBodyText:  { fontSize: 15, color: BRAND.dark, lineHeight: 24 },
  detailActions:  { flexDirection: 'row', gap: 12 },
  detailActionBtn:{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 12, borderRadius: 10, borderWidth: 1 },
  detailActionText:{ fontSize: 14, fontWeight: '600' },
  // AI modal
  aiLabel:        { fontSize: 14, fontWeight: '600', color: BRAND.dark, marginBottom: 10 },
  aiInput:        { borderWidth: 1, borderColor: BRAND.line, borderRadius: 10, padding: 14, fontSize: 15, color: BRAND.dark, minHeight: 120, backgroundColor: BRAND.white },
  aiSuggestion:   { backgroundColor: BRAND.primary + '15', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16 },
  aiSuggestionText:{ fontSize: 12, color: BRAND.primary, fontWeight: '600' },
  aiGenBtn:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: BRAND.primary, paddingVertical: 14, borderRadius: 12, marginTop: 20 },
  aiGenBtnText:   { color: BRAND.white, fontSize: 15, fontWeight: '700' },
  // Template modal
  tplSelectItem:  { flexDirection: 'row', alignItems: 'center', backgroundColor: BRAND.white, borderRadius: 10, padding: 14, marginBottom: 8 },
  tplSelectName:  { fontSize: 15, fontWeight: '600', color: BRAND.dark },
  tplSelectSubject: { fontSize: 13, color: BRAND.sub, marginTop: 3 },
  // Contact picker
  contactItem:    { flexDirection: 'row', alignItems: 'center', backgroundColor: BRAND.white, borderRadius: 10, padding: 14, marginBottom: 8 },
  contactAvatar:  { width: 42, height: 42, borderRadius: 21, backgroundColor: BRAND.primary, justifyContent: 'center', alignItems: 'center' },
  contactInitial: { color: BRAND.white, fontSize: 16, fontWeight: '700' },
  contactName:    { fontSize: 14, fontWeight: '600', color: BRAND.dark },
  contactEmail:   { fontSize: 12, color: BRAND.primary, marginTop: 1 },
  contactCompany: { fontSize: 12, color: BRAND.sub, marginTop: 1 },
  // Filters
  filterSectionTitle: { fontSize: 13, fontWeight: '700', color: BRAND.sub, marginTop: 16, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 },
  filterItem:     { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 10, marginBottom: 4, backgroundColor: BRAND.white },
  filterItemActive: { backgroundColor: '#eff6ff' },
  filterItemText: { flex: 1, fontSize: 15, color: BRAND.dark },
  // Schedule
  schedOption:    { flexDirection: 'row', alignItems: 'center', paddingVertical: 14, paddingHorizontal: 16, backgroundColor: BRAND.white, borderRadius: 12, marginBottom: 10 },
});